/**
 * WorksheetsGate — combines the legacy dev-host gate
 * (`SHOW_WORKSHEETS`) with the PostHog `worksheets_enabled` flag.
 *
 * Mounts the worksheets route only when EITHER:
 *   - the request is from a dev hostname (localhost, dev.*) per
 *     launchFlags.ts, OR
 *   - the `worksheets_enabled` PostHog flag is true for this teacher.
 *
 * Why the OR (rather than tightening to flag-only): worksheets is
 * still actively iterated on internally, and dev access via the
 * dev.teacherdashboard.ai host shouldn't require flipping a flag
 * just to use the tool locally. The flag is for graduated production
 * rollout — start with a beta cohort by distinct_id, then ramp
 * `rollout_percentage` to GA when ready.
 *
 * On the disabled path we redirect to /dashboard rather than render
 * an "unavailable" banner — teachers who don't have access shouldn't
 * even discover the URL exists. (Contrast `<ToolGuard>` which renders
 * a banner: that's for the kill-switch case, where teachers KNOW the
 * tool exists and are mid-task.)
 */
import React from "react";
import { Navigate } from "react-router-dom";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { SHOW_WORKSHEETS } from "../../config/launchFlags";

interface WorksheetsGateProps {
  children: React.ReactNode;
}

export default function WorksheetsGate({ children }: WorksheetsGateProps) {
  const flagEnabled = useFeatureFlag<boolean>("worksheets_enabled", false);
  if (!flagEnabled && !SHOW_WORKSHEETS) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}
